import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import logo from 'assets/images/image.png';

export const AUTHENTICATION_TIMEOUT_MS = 10000;
const AUTHENTICATION_REVEAL_DELAY_MS = 600;

const rotateProgress = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const AuthenticationLoading = ({
    primaryText = 'Checking your session…',
    progressLabel = 'Checking your secure session',
    revealDelay = AUTHENTICATION_REVEAL_DELAY_MS,
    secondaryText = 'This will only take a moment'
}) => {
    const [isVisible, setIsVisible] = useState(revealDelay <= 0);

    useEffect(() => {
        if (revealDelay <= 0) return undefined;

        const revealTimer = window.setTimeout(() => setIsVisible(true), revealDelay);
        return () => window.clearTimeout(revealTimer);
    }, [revealDelay]);

    return (
        <Box
            component="main"
            sx={{
                alignItems: 'center',
                bgcolor: 'zimttech.canvas',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '100vh',
                overflow: 'hidden',
                px: 3,
                py: 4,
                '@supports (height: 100dvh)': {
                    minHeight: '100dvh'
                }
            }}
        >
            {isVisible && (
                <Box
                    role="status"
                    aria-live="polite"
                    sx={{
                        maxWidth: 360,
                        textAlign: 'center',
                        width: '100%'
                    }}
                >
                    <Box component="img" src={logo} alt="ZIM-TTECH" sx={{ display: 'block', height: 'auto', mx: 'auto', width: 164 }} />
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, justifyContent: 'center', mt: 3 }}>
                        <CircularProgress
                            aria-label={progressLabel}
                            size={26}
                            thickness={4.5}
                            value={76}
                            variant="determinate"
                            sx={{
                                animation: `${rotateProgress} 1.15s linear infinite`,
                                color: 'zimttech.brand',
                                flexShrink: 0,
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none'
                                }
                            }}
                        />
                        <Typography sx={{ color: 'zimttech.ink', fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}>
                            {primaryText}
                        </Typography>
                    </Box>
                    {secondaryText && (
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.5, mt: 1 }}>
                            {secondaryText}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

AuthenticationLoading.propTypes = {
    primaryText: PropTypes.string,
    progressLabel: PropTypes.string,
    revealDelay: PropTypes.number,
    secondaryText: PropTypes.string
};

export default AuthenticationLoading;
